import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import ModalWindow from '../../Modals/ModalWindow/ModalWindow';
import ModalButton from '../../Modals/ModalWindow/ModalButton/ModalButton';
import PremiumRewardModal from '../../Modals/PremiumRewardModal/PremiumRewardModal';
import FactionPurchaseRewardModal from '../../Modals/FactionPurchaseRewardModal/FactionPurchaseRewardModal';
import {
  selectIsTutorialRepeatRun,
  selectTutorialChapterId
} from '../../../redux/reducers/privateStateReducer/selectors';
import {sendButtonClickEvent, sendTutorialEvent} from '../../../analytics/gameAnalytics';
import {
  getTutorialModalKey,
  getTutorialRunKind,
  TUTORIAL_ANALYTICS_EVENTS,
  TUTORIAL_CHAPTER_COMPLETE_MODAL_ID
} from '../../../utils/tutorial/tutorialAnalyticsIds';
import {getTutorialReplayRecorder} from '../../../utils/tutorial/replay/tutorialReplayRecorderRegistry';
import {
  checkIfAllTutorialChaptersCompleted,
  getTutorialChapter,
  TUTORIAL_CHAPTERS,
} from '../../../configs/tutorial';
import {markBotMatchPromoPending} from '../../../utils/menuPromo/botMatchPromoStorage';
import {trackAdGoal, AD_GOALS} from '../../../analytics/adTracking';
import {
  setAvailableFactions,
  setCompletedTutorialChapters,
  setPremiumExpiresAt,
  tutorialCompletionSaved,
  updatePlayerRewards
} from '../../../redux/actions';
import {API_BASE} from '../../../utils/apiConfig';
import {REGISTRY_SINGLETON} from '../../../scenes/Registry';
import {getChestIconPath} from '../../../utils/inventory/getChestIconPath';
import {getFactionConfig} from '../../../utils/factions/getFactionConfig';
import {getFactionIconPath} from '../../../utils/factions/getFactionIconPath';
import {ALL_CHEST_CONFIGS} from '../../../configs/chests';
import {HUMANS_QUOTES} from '../../../texts/quotes/factions/humans';
import './TutorialChapterCompleteModal.css';

/**
 * Флоу завершения главы обучения. На монтировании сохраняет прохождение на
 * сервере; при ПЕРВОМ прохождении сервер начисляет и возвращает награду
 * главы (конфиг chapter.reward).
 *
 * Порядок показа: пока открыты игровые модалки (захват города, итоги раунда —
 * isWaitingForGameModals от хоста) ничего не рендерится; затем поздравление;
 * и только когда игрок уже на поздравлении — поверх него (с короткой
 * паузой) наградная модалка:
 * - премиум (глава 1): штатная модалка премиум-дара (как при выпадении из
 *   сундука); после «Забрать дар» остается поздравление с краткой строкой о
 *   награде; кнопки коллекции у главы 1 нет вовсе;
 * - сундук (глава 2, а также глава 3 для тех, у кого награда-фракция уже
 *   открыта — см. getTutorialChapterReward): отдельной модалки нет — сундук с
 *   названием крупно в самом поздравлении, кнопка — «Открыть в коллекции»;
 * - фракция (глава 3): штатная модалка открытия фракции поверх поздравления,
 *   кнопка — «Посмотреть в коллекции».
 * При ПОВТОРНОМ прохождении награды нет вовсе — тогда ни наградной модалки,
 * ни кнопки коллекции: в поздравлении остаются только «Далее» и выход в меню.
 * У последней главы вместо «Далее» — «В битву»: готовая локальная партия
 * против двух ботов (onBattle, см. configs/newbieBattle).
 * Локальный стейт (премиум/фракции/бейдж «Новое» в коллекции) обновляется
 * сразу из ответа — без перезагрузки страницы.
 */

const COMPLETE_MODAL_PAGE_ID = 'TutorialChapterCompleteModal';

// Пауза между появлением поздравления и наградной модалкой поверх него:
// игрок должен успеть увидеть, что глава пройдена
const REWARD_MODAL_DELAY_MS = 800;

// Тексты кнопки перехода в коллекцию по главам (у главы 1 кнопки нет)
const COLLECTION_BUTTON_TEXT_BY_CHAPTER = {
  tutorial_chapter_2: 'Открыть в коллекции',
  tutorial_chapter_3: 'Посмотреть в коллекции',
};

// «В битву»: запрос лобби в пути — кнопка неактивна; не удался — подсказка
const BATTLE_REQUEST_STATES = Object.freeze({
  IDLE: 'idle',
  PENDING: 'pending',
  FAILED: 'failed',
});

const TutorialChapterCompleteModal = ({
                                        isWaitingForGameModals = false,
                                        onNextChapter,
                                        onOpenInventory,
                                        onExitToMenu,
                                        onBattle,
                                      }) => {
  const dispatch = useDispatch();
  const chapterId = useSelector(selectTutorialChapterId);
  const isRepeatRun = useSelector(selectIsTutorialRepeatRun);
  const isCompletionSaved = useSelector((state) => state.private.tutorial.isCompletionSaved);
  const availableFactions = useSelector((state) => state.private.auth.availableFactions);
  // Снимок на момент запроса: по нему ниже видно, что именно этот ответ
  // закрыл обучение целиком (последняя глава пройдена впервые)
  const completedTutorialChapters = useSelector((state) => state.private.auth.completedTutorialChapters);
  const saveRequestedRef = useRef(false);

  // null — ответ сервера еще не пришел; {reward: null} — награды нет
  // (повторное прохождение или ошибка сети)
  const [saveResult, setSaveResult] = useState(null);
  const [isRewardModalShown, setIsRewardModalShown] = useState(false);
  const [isRewardModalClosed, setIsRewardModalClosed] = useState(false);
  const [battleRequestState, setBattleRequestState] = useState(BATTLE_REQUEST_STATES.IDLE);
  const isMountedRef = useRef(true);
  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  const chapter = getTutorialChapter(chapterId);
  const chapterIndex = TUTORIAL_CHAPTERS.findIndex((item) => item.id === chapterId);
  const nextChapter = chapterIndex >= 0 ? TUTORIAL_CHAPTERS[chapterIndex + 1] : null;

  // Поздравление видно, когда ответ сервера получен и игровых модалок нет
  const isVisible = Boolean(saveResult) && !isWaitingForGameModals;

  // Звук — в момент реального появления поздравления, один раз
  const soundPlayedRef = useRef(false);
  useEffect(() => {
    if (isVisible && !soundPlayedRef.current) {
      soundPlayedRef.current = true;
      REGISTRY_SINGLETON.getSoundManager().playGameStart();
    }
  }, [isVisible]);

  // Глава обучения пройдена: цель показывает, доходит ли рекламный трафик до
  // самой игры. Шлется вместе с показом поздравления — ровно один раз на главу
  const chapterGoalSentRef = useRef(false);
  useEffect(() => {
    if (!isVisible || chapterGoalSentRef.current) {
      return;
    }
    chapterGoalSentRef.current = true;
    trackAdGoal(AD_GOALS.TUTORIAL_CHAPTER_COMPLETED, {params: {chapterId}});
    // Последний узел воронки шагов главы — поздравление (номер «шагов + 1»)
    const modalKey = getTutorialModalKey(chapterId, TUTORIAL_CHAPTER_COMPLETE_MODAL_ID);
    if (modalKey) {
      sendTutorialEvent(chapterId, TUTORIAL_ANALYTICS_EVENTS.MODAL_SHOWN, modalKey, getTutorialRunKind(isRepeatRun));
    }
  }, [isVisible, chapterId, isRepeatRun]);

  // Наградная модалка — только когда игрок уже на поздравлении, с паузой
  const hasRewardModal = saveResult?.reward?.type === 'premium' || saveResult?.reward?.type === 'faction';
  useEffect(() => {
    if (!isVisible || !hasRewardModal || isRewardModalShown || isRewardModalClosed) {
      return undefined;
    }
    const timeoutId = setTimeout(() => setIsRewardModalShown(true), REWARD_MODAL_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [isVisible, hasRewardModal, isRewardModalShown, isRewardModalClosed]);

  // Сохранение прогресса (и получение награды) — один раз на показ модалки
  useEffect(() => {
    if (!chapterId || saveRequestedRef.current) {
      return;
    }
    saveRequestedRef.current = true;

    if (isCompletionSaved) {
      setSaveResult({reward: null});
      return;
    }

    // Запись прохождения в истории матчей создана на старте главы — завершение
    // переводит в пройденные именно ее (по идентификатору прохождения: он
    // известен, даже если ответ на создание записи не дошел). Записи может и
    // не быть вовсе — тогда глава сохраняется как раньше, без нее
    const runInfo = getTutorialReplayRecorder()?.getRunInfo();
    const runId = runInfo?.chapterId === chapterId ? runInfo.runId : null;
    fetch(`${API_BASE}/users/me/tutorial-chapters/complete`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      credentials: 'include',
      body: JSON.stringify(runId ? {chapterId, runId} : {chapterId}),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((data) => {
        dispatch(tutorialCompletionSaved());
        if (Array.isArray(data.completedTutorialChapters)) {
          dispatch(setCompletedTutorialChapters(data.completedTutorialChapters));
          // Обучение пройдено целиком впервые — следующий заход в главное
          // меню откроет приглашение в первый бой с ботами
          // (BotMatchPromoModal). Повторное прохождение последней главы
          // отметку не ставит: приглашение и так напоминает о себе по
          // суточному правилу, пока партий нет
          if (!checkIfAllTutorialChaptersCompleted(completedTutorialChapters)
            && checkIfAllTutorialChaptersCompleted(data.completedTutorialChapters)) {
            markBotMatchPromoPending();
          }
        }

        // Обновляем локальный стейт наградой — визуализация без перезагрузки
        const reward = data.reward || null;
        if (reward?.type === 'premium' && reward.premiumExpiresAt) {
          dispatch(setPremiumExpiresAt(reward.premiumExpiresAt));
        } else if (reward?.type === 'chest') {
          dispatch(updatePlayerRewards(0, undefined, true));
        } else if (reward?.type === 'faction') {
          dispatch(updatePlayerRewards(0, undefined, true));
          const factionConfig = getFactionConfig(reward.factionId);
          const alreadyListed = (availableFactions || []).some(
            (faction) => faction.id === reward.factionId && faction.isAvailable
          );
          if (!alreadyListed) {
            const withoutReward = (availableFactions || []).filter((faction) => faction.id !== reward.factionId);
            dispatch(setAvailableFactions([
              ...withoutReward,
              {id: reward.factionId, name: factionConfig?.name || reward.factionId, isAvailable: true},
            ]));
          }
        }
        setSaveResult({reward});
      })
      .catch((error) => {
        // Не блокируем поздравление: прогресс досохранится при следующем
        // прохождении, а сервер идемпотентен ($addToSet)
        console.error('[Tutorial] Failed to save chapter completion:', error);
        setSaveResult({reward: null});
      });
  }, [chapterId, isCompletionSaved, dispatch, availableFactions, completedTutorialChapters]);

  if (!chapter) {
    return null;
  }

  // Ждем ответ сервера (модалки наград зависят от того, первое ли это
  // прохождение) и закрытия игровых модалок
  if (!isVisible) {
    return null;
  }

  const reward = saveResult.reward;

  // Наградная модалка поверх поздравления (z-index оверлея выше ModalWindow)
  let rewardModal = null;
  if (reward && isRewardModalShown && !isRewardModalClosed) {
    if (reward.type === 'premium') {
      rewardModal = (
        <PremiumRewardModal
          days={reward.days}
          onClaim={() => setIsRewardModalClosed(true)}
        />
      );
    } else if (reward.type === 'faction') {
      rewardModal = (
        <FactionPurchaseRewardModal
          factionId={reward.factionId}
          onClaim={() => setIsRewardModalClosed(true)}
        />
      );
    }
  }

  // Кнопка коллекции — только когда награда реально выдана в этот раз: при
  // повторном прохождении главы сервер награду не начисляет (reward === null),
  // и звать в коллекцию не за чем.
  // Сама награда зависит еще и от игрока (глава 3 отдает сундук тем, у кого
  // фракция уже открыта — см. getTutorialChapterReward), поэтому текст кнопки
  // ведет фактическая награда, а не только номер главы: сундук всегда
  // «Открыть»
  const collectionButtonText = reward && (reward.type === 'chest'
    ? 'Открыть в коллекции'
    : COLLECTION_BUTTON_TEXT_BY_CHAPTER[chapterId]);
  const chestConfig = reward?.type === 'chest'
    ? ALL_CHEST_CONFIGS.find((config) => config.id === reward.chestId)
    : null;
  const factionConfig = reward?.type === 'faction' ? getFactionConfig(reward.factionId) : null;

  // Обучение пройдено целиком — сразу в бой с ботами, пока уроки свежи.
  // onBattle отвечает, ушел ли игрок в партию: при успехе обучение
  // закрывается вместе с поздравлением
  const handleBattle = () => {
    if (battleRequestState === BATTLE_REQUEST_STATES.PENDING) {
      return;
    }
    sendButtonClickEvent(COMPLETE_MODAL_PAGE_ID, 'battle', chapterId);
    setBattleRequestState(BATTLE_REQUEST_STATES.PENDING);
    Promise.resolve(onBattle())
      .catch(() => false)
      .then((isLobbyEntered) => {
        if (isMountedRef.current) {
          setBattleRequestState(isLobbyEntered ? BATTLE_REQUEST_STATES.IDLE : BATTLE_REQUEST_STATES.FAILED);
        }
      });
  };

  return (
    <>
      {/* Обучение идет за Людей — и цитата под поздравлением из их набора */}
      <ModalWindow className={'tutorial-chapter-complete-modal'} showQuote quotes={HUMANS_QUOTES}>
        <h2 className={'tutorial-chapter-complete-modal_title'}>Поздравляем!</h2>
        <p className={'tutorial-chapter-complete-modal_text'}>
          {chapter.shortTitle} Обучения пройдена.
        </p>

        {reward?.type === 'premium' && (
          <div className={'tutorial-chapter-complete-modal_reward-line'}>
            <img
              className={'tutorial-chapter-complete-modal_reward-icon'}
              src={'/img/icons/premium.png'}
              alt={'Премиум'}
            />
            <span>Награда: <b>Премиум на {reward.days} дня</b></span>
          </div>
        )}

        {reward?.type === 'chest' && (
          <div className={'tutorial-chapter-complete-modal_chest'}>
            <img
              className={'tutorial-chapter-complete-modal_chest-image'}
              src={getChestIconPath(reward.chestId)}
              alt={chestConfig?.name || reward.chestId}
            />
            <span
              className={'tutorial-chapter-complete-modal_chest-name'}
              style={chestConfig?.textColor ? {color: chestConfig.textColor} : undefined}
            >
              {chestConfig?.name || 'Сундук'}
            </span>
            <span className={'tutorial-chapter-complete-modal_reward-note'}>
              Награда уже в твоей коллекции!
            </span>
          </div>
        )}

        {reward?.type === 'faction' && (
          <div className={'tutorial-chapter-complete-modal_reward-line'}>
            <img
              className={'tutorial-chapter-complete-modal_reward-icon'}
              src={getFactionIconPath(reward.factionId)}
              alt={factionConfig?.name || reward.factionId}
            />
            <span>Награда: фракция <b>{factionConfig?.name || reward.factionId}</b></span>
          </div>
        )}

        <div className={'tutorial-chapter-complete-modal_buttons'}>
          {!nextChapter && onBattle && (
            <ModalButton
              onClick={handleBattle}
              text={'В битву'}
              className={'tutorial-chapter-complete-modal_next-button'}
              disabled={battleRequestState === BATTLE_REQUEST_STATES.PENDING}
            />
          )}
          {battleRequestState === BATTLE_REQUEST_STATES.FAILED && (
            <p className={'tutorial-chapter-complete-modal_battle-error'}>
              Не удалось собрать битву. Попробуй еще раз.
            </p>
          )}
          {nextChapter && (
            <ModalButton
              onClick={() => {
                // Переход между главами — главная конверсия после самой главы:
                // глава в значении кнопки, иначе переходы 1→2 и 2→3 слились бы
                sendButtonClickEvent(COMPLETE_MODAL_PAGE_ID, 'next_chapter', chapterId);
                onNextChapter(nextChapter.id);
              }}
              text={`Далее: ${nextChapter.shortTitle}`}
              className={'tutorial-chapter-complete-modal_next-button'}
            />
          )}
          {collectionButtonText && (
            <ModalButton
              onClick={() => {
                sendButtonClickEvent(COMPLETE_MODAL_PAGE_ID, 'collection', chapterId);
                onOpenInventory();
              }}
              text={collectionButtonText}
            />
          )}
          <ModalButton
            onClick={() => {
              sendButtonClickEvent(COMPLETE_MODAL_PAGE_ID, 'main_menu', chapterId);
              onExitToMenu();
            }}
            text={'В главное меню'}
          />
        </div>
      </ModalWindow>
      {rewardModal}
    </>
  );
};

export default TutorialChapterCompleteModal;
